import styled from 'styled-components';
import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';

import { buildSectionLinkUrl } from 'homepage/utils/buildSectionLinkUrl';
import { DESKTOP_PADDING, MOBILE_PADDING } from 'homepage/styles/constants';
import { fetchDetailedProductListSection } from 'homepage/services/homepageApi';
import { handleSectionClick } from 'homepage/utils/handleSectionClick';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { setCurrentFeedData } from 'store/actions/homepageActions';
import { useIsElementInViewport } from 'shared/hooks/useIsElementInViewport';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useAbortController } from 'shared/hooks/useAbortController';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';

import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { IConstructorUserParams } from 'search/types/ConstructorUserParams';
import { IFeedSection } from 'homepage/types/Feed';
import { IModuleTrackingData } from 'homepage/types/ModuleTrackingData';
import { FeatureFlags } from 'featureFlags/constants';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';

const ProductListTableCarousel = dynamic(() =>
  import('homepage/components/detailedProductListSection/ProductListTableCarousel').then(
    (mod) => mod.ProductListTableCarousel,
  ),
);

const HomepageModuleHeader = dynamic(
  () =>
    import('homepage/components/sections/HomepageModuleHeader').then(
      (mod) => mod.HomepageModuleHeader,
    ),
  { ssr: false },
);

interface IDetailedProductListSection extends IFeedSection {
  constructorUserParams?: IConstructorUserParams;
  constructorResponse: IConstructorBrowseResponse;
  moduleTrackingData: IModuleTrackingData;
  sectionIndex: number;
}

export const DetailedProductListSection = (section: IDetailedProductListSection) => {
  const storeDispatch = useStoreDispatch();
  const moduleRef = useRef();
  const initialInViewport = useIsElementInViewport(moduleRef);
  const { signal } = useAbortController()();

  const { isLoading } = useStoreSelector((state) => state.homepage);
  const {
    constructorUserParams,
    constructorResponse,
    searchConfig,
    sectionIndex,
    moduleTrackingData,
    title,
  } = section;

  const isConsumerSearch = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);
  const [products, setProducts] = useState(constructorResponse);
  const [isModuleInViewport, setIsModuleInViewport] = useState(initialInViewport);
  const isMounted = useIsMounted();

  useEffect(() => {
    const getProducts = async (abortSignal) => {
      const response = await fetchDetailedProductListSection({
        searchConfig,
        headers: constructorUserParams,
        abortSignal,
        isConsumerSearch,
      });

      if (isMounted()) setProducts(response);
    };

    if (isMounted()) {
      if (!isModuleInViewport) {
        setIsModuleInViewport(initialInViewport);
      } else if (typeof constructorResponse !== 'undefined' && !products) {
        setProducts(constructorResponse);
      } else if (
        isModuleInViewport &&
        !isLoading &&
        !products &&
        constructorUserParams &&
        !constructorResponse
      ) {
        void getProducts(signal);
      }
    }
  }, [
    initialInViewport,
    isModuleInViewport,
    isLoading,
    products,
    constructorResponse,
    isConsumerSearch,
    constructorUserParams,
  ]);

  useEffect(() => {
    if (products) {
      storeDispatch(setCurrentFeedData({ index: sectionIndex, constructorResponse: products }));
    }
  }, [products]);

  const linkUrl = buildSectionLinkUrl(searchConfig, products?.response?.groups, isConsumerSearch);

  return products?.response?.results ? (
    <DetailedProductListSection.Container ref={moduleRef}>
      <HomepageModuleHeader
        addPadding
        count={products?.response?.total_num_results}
        onClick={() =>
          handleSectionClick({
            ...(moduleTrackingData || {}),
            service_name: isConsumerSearch ? 'consumer_search' : 'constructor',
          })
        }
        linkUrl={linkUrl}
        title={title}
      />
      <ProductListTableCarousel
        moduleTrackingData={moduleTrackingData}
        results={products?.response?.results}
      />
    </DetailedProductListSection.Container>
  ) : (
    <DetailedProductListSection.Container ref={moduleRef} />
  );
};

DetailedProductListSection.Container = styled.div`
  margin: 32px 0px;
  width: 100%;
  max-width: 100%;
  min-height: 465px;
  overflow-x: scroll;
  padding-left: ${MOBILE_PADDING};
  ${hideScrollbars}

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 60px 0px;
    padding-left: ${DESKTOP_PADDING};
    min-height: 536.5px;
  }
`;
